import {Component, OnInit, ViewChild} from "@angular/core";
import {SharedService} from "../../services/sharedService";
import {AdminService} from "../../services/admin.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {DocumentService} from "../../services/document.service";
import {forkJoin, of} from "rxjs";
import {catchError, tap} from "rxjs/operators";
import {saveAs} from 'file-saver';
import {DOC_HREF_PREFIX, CARDS_KEY} from './help.component';
import {TemplateEditor} from '../template-editor/template-editor.component';
import {HelpCard, DEFAULT_HELP_CARDS, mergeHelpCards, textFor} from './help-cards';

const KEY = 'HOME_HELP_TEXT';
const DOC_ENTITY = 'help';
const DOC_ENTITY_ID = 'global';

/**
 * Where an administrator rewrites the help cards.
 *
 * Every card is listed with its title and text in every offered language, one
 * under the other, because editing help is mostly comparing translations of the
 * same sentence: a French card that no longer says what the English one says is
 * visible here and nowhere else.
 *
 * Only what DIFFERS from the shipped text is stored. That is not a size
 * optimisation -- it is what lets an improved default reach a deployment that
 * never touched that card, while leaving alone every card somebody has
 * deliberately rewritten.
 */
@Component({
    selector: 'app-help-editor',
    templateUrl: './help-editor.component.html',
    styleUrls: ['./help-editor.component.css'],
    standalone: false
})
export class HelpEditorComponent implements OnInit {

    // ── The cards ─────────────────────────────────────────────────────────
    cards: HelpCard[] = [];
    /** Offered languages, from the settings record. English is always present. */
    languages: string[] = ['en'];
    savingCards = false;

    // ── The older free-text block ─────────────────────────────────────────
    data: string;
    savedOk: string = '';
    loaded = false;
    adminSettings: any = {};
    documents: any[] = [];

    // Not static: the editor sits inside an @if (loaded) block.
    @ViewChild(TemplateEditor) templateEditor: TemplateEditor;

    constructor(private settings: AdminService,
                private shared: SharedService,
                private alertService: ToastrService,
                private translate: TranslateService,
                private documentService: DocumentService) {
        this.shared.getLan$().subscribe(() => this.loadHelpText());
    }

    ngOnInit(): void {
        this.loadHelpText();
        this.loadDocuments();
    }

    loadHelpText() {
        this.loaded = false;
        this.settings.getAdminSettings().subscribe(settings => {
            if (settings && settings.length > 0)
                this.adminSettings = settings[0];
            else
                this.adminSettings = {};

            this.data = this.adminSettings[KEY]?.[this.shared.getLan()] || '';
            // Same rule the header uses: a deployment that has never chosen
            // offers English and French.
            const offered = Array.isArray(this.adminSettings.languages)
                ? this.adminSettings.languages.slice()
                : ['en', 'fr'];
            if (offered.indexOf('en') === -1) offered.unshift('en');
            this.languages = offered;

            // A deep copy: the editor writes into these objects, and mutating
            // DEFAULT_HELP_CARDS would leave the shipped text unrecoverable
            // for the rest of the session.
            this.cards = JSON.parse(JSON.stringify(mergeHelpCards(this.adminSettings[CARDS_KEY])));
            this.loaded = true;
        });
    }

    setText(card: HelpCard, field: 'title' | 'body', lang: string, value: string) {
        card[field][lang] = value;
    }

    /** Whether this card still says exactly what it shipped saying. */
    isEdited(id: string): boolean {
        const shipped = DEFAULT_HELP_CARDS.find(c => c.id === id);
        const current = this.cards.find(c => c.id === id);
        if (!shipped || !current) return false;
        return this.languages.some(lang =>
            textFor(current.title, lang) !== textFor(shipped.title, lang) ||
            textFor(current.body, lang) !== textFor(shipped.body, lang));
    }

    get editedCount(): number {
        return this.cards.filter(c => this.isEdited(c.id)).length;
    }

    resetCard(id: string) {
        const shipped = DEFAULT_HELP_CARDS.find(c => c.id === id);
        const at = this.cards.findIndex(c => c.id === id);
        if (!shipped || at === -1) return;
        this.cards[at] = JSON.parse(JSON.stringify(shipped));
    }

    resetAll() {
        if (!confirm(this.translate.instant('HELP.RESET_ALL_CONFIRM'))) return;
        this.cards = JSON.parse(JSON.stringify(DEFAULT_HELP_CARDS));
    }

    /**
     * Stores only the text that differs from what shipped.
     *
     * A card left alone is not stored at all, so a later release that improves
     * its wording reaches this deployment -- while a card somebody rewrote
     * keeps the words they chose.
     */
    buildOverrides(): any[] {
        const out: any[] = [];
        for (const card of this.cards) {
            const shipped = DEFAULT_HELP_CARDS.find(c => c.id === card.id);
            if (!shipped) continue;
            const entry: any = { id: card.id };
            let changed = false;

            for (const field of ['title', 'body'] as const) {
                const diff: any = {};
                for (const lang of this.languages) {
                    const value = (card[field][lang] || '').trim();
                    if (!value) continue;                       // blank means "use the shipped text"
                    if (value === (shipped[field][lang] || '').trim()) continue;
                    diff[lang] = value;
                    changed = true;
                }
                if (Object.keys(diff).length) entry[field] = diff;
            }

            if (changed) out.push(entry);
        }
        return out;
    }

    saveCards() {
        this.savingCards = true;
        const payload: any = { id: this.adminSettings.id };
        payload[CARDS_KEY] = this.buildOverrides();

        this.settings.saveAdminSettings(payload).subscribe({
            next: (data) => {
                this.savingCards = false;
                if (data) this.adminSettings = Array.isArray(data) ? data[0] : data;
                this.alertService.success(this.translate.instant('HELP.CARDS_SAVED'));
            },
            error: () => {
                this.savingCards = false;
                this.alertService.error(this.translate.instant('HELP.CARDS_NOT_SAVED'));
            },
        });
    }

    // ── The older free-text block, unchanged ──────────────────────────────

    loadDocuments() {
        this.documentService.list$(DOC_ENTITY, DOC_ENTITY_ID).subscribe(
            docs => { this.documents = docs || []; },
            () => {
                this.documents = [];
                this.alertService.error('Failed to load attachments');
            }
        );
    }

    onFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files.length) return;
        const files = Array.from(input.files);
        input.value = '';
        forkJoin(files.map(file =>
            this.documentService.add$(DOC_ENTITY, DOC_ENTITY_ID, file).pipe(
                tap(() => this.alertService.success(`${file.name} uploaded successfully`)),
                catchError(() => {
                    this.alertService.error(`Failed to upload ${file.name}`);
                    return of(null);
                })
            )
        )).subscribe(() => this.loadDocuments());
    }

    insertLink(doc: any) {
        // Uses the editor component's own API. This previously reached through
        // the global tinymce registry by element id, which no longer exists.
        const editor = this.templateEditor;
        if (editor && doc?.id && doc?.originalname) {
            const safeId = String(doc.id).replace(/[^a-zA-Z0-9\-_]/g, '');
            const safeName = doc.originalname
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            // Fragment href rather than data-document-id: Angular's sanitizer
            // strips data-* attributes, and the help page now renders this
            // content sanitized instead of through bypassSecurityTrustHtml.
            editor.insertHtml(`<a href="${DOC_HREF_PREFIX}${safeId}">${safeName}</a>`);
        }
    }

    downloadDocument(doc: any) {
        this.documentService.get$(doc.id).subscribe(
            (blob: Blob) => { saveAs(blob, doc.originalname); },
            () => { this.alertService.error(`Failed to download ${doc.originalname}`); }
        );
    }

    deleteDocument(doc: any) {
        if (!confirm(`Are you sure you want to delete "${doc.originalname}"?`)) return;
        this.documentService.delete$(doc.id).subscribe(
            () => {
                this.alertService.success(`${doc.originalname} deleted`);
                this.loadDocuments();
            },
            () => { this.alertService.error(`Failed to delete ${doc.originalname}`); }
        );
    }

    save() {
        this.savedOk = '';
        const tempData = {id: this.adminSettings.id};
        const lang = {};
        lang[this.shared.getLan()] = '';
        tempData[KEY] = lang;
        tempData[KEY][this.shared.getLan()] = this.data;
        this.settings.saveAdminSettings(tempData).subscribe(data => {
            this.savedOk = 'Ok';
            this.alertService.success('Help text saved successfully');
            this.adminSettings = data[0];
        });
    }

    keyupHandlerFunction($event) {
        this.data = $event;
    }
}
